import { JSX, ReactNode, useCallback } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  ArrowRightStartOnRectangleIcon,
  BeakerIcon,
  BookOpenIcon,
  EllipsisHorizontalIcon,
  FolderIcon,
  MoonIcon,
  SunIcon,
  SwatchIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';
import clsx from 'clsx';
import { Boxes, GitBranch } from 'lucide-react';

import { getEnvironment } from '../config';
import { useOptionalPageChrome } from '../contexts/PageChromeContext';
import { useLogout } from '../hooks/useLogout';
import { useTheme } from '../hooks/useTheme';
import { usePatternsStore } from '../stores/patternsStore';
import { useUIStore } from '../stores/uiStore';

import { ApiStatus } from './ApiStatus';
import { Logo } from './Logo';
import { MenuDesktop } from './MenuDesktop';
import { MenuMobile } from './MenuMobile';
import { NavDropdown } from './NavDropdown';
import type { NavDropdownItem } from './NavDropdown';
import { OrgSwitcher } from './OrgSwitcher';

interface HeaderProps {
  endContent?: ReactNode;
  layout?: 'default' | 'logout' | 'minimal';
}

export function Header(props: HeaderProps): JSX.Element {
  const { endContent, layout = 'default' } = props;
  const navigate = useNavigate();
  const { handleLogout } = useLogout();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { isDevelopment } = getEnvironment();
  const lastPatternsPath = usePatternsStore(s => s.lastPatternsPath);
  const setSelectedRepositoryId = useUIStore(s => s.setSelectedRepositoryId);
  const chrome = useOptionalPageChrome();

  const onLogout = useCallback((): void => {
    void handleLogout()
      .then(() => navigate('/login'))
      .catch(() => navigate('/login'));
  }, [handleLogout, navigate]);

  const selectedClass = 'text-foreground nav-tab-active';
  const unselectedClass = 'text-muted hover:text-foreground';
  const navLinkClassName = ({ isActive }: { isActive: boolean }): string =>
    `inline-flex items-center px-6 pt-4 pb-5 text-sm font-medium transition-colors ${isActive ? selectedClass : unselectedClass}`;

  let navigation: ReactNode;
  let content: ReactNode;
  let chromeSlot: ReactNode;
  let chromeSubtitle: ReactNode;

  if (endContent) {
    content = endContent;
  } else if (layout === 'default') {
    content = (
      <>
        <div className="hidden lg:block">
          <OrgSwitcher />
        </div>
        <div className="hidden lg:block">
          <ApiStatus />
        </div>
        <Button aria-label="Toggle dark mode" isIconOnly onPress={toggleDarkMode} variant="light">
          {isDarkMode ? <SunIcon className="h-6 w-6" /> : <MoonIcon className="h-6 w-6" />}
        </Button>
        <div className="hidden lg:block">
          <MenuDesktop />
        </div>
        <MenuMobile isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
      </>
    );
  } else if (layout === 'logout') {
    content = (
      <Button
        color="primary"
        className="text-muted"
        onPress={onLogout}
        startContent={<ArrowRightStartOnRectangleIcon className="size-5" />}
        variant="bordered"
      >
        Logout
      </Button>
    );
  }

  if (layout === 'default') {
    const moreItems: NavDropdownItem[] = [
      {
        to: '/jira',
        label: 'Jira',
        icon: (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.34V2.84a.84.84 0 0 0-.84-.84h-9.63zM6.77 6.8a4.36 4.36 0 0 0 4.35 4.35h1.78v1.7a4.35 4.35 0 0 0 4.34 4.34V7.63a.84.84 0 0 0-.84-.84H6.77zM2 11.6c0 2.4 1.95 4.34 4.35 4.34h1.78v1.72c.01 2.39 1.95 4.34 4.35 4.34v-9.56a.84.84 0 0 0-.84-.84H2z" />
          </svg>
        ),
      },
      {
        to: lastPatternsPath,
        label: 'Patterns',
        icon: <SwatchIcon className="h-5 w-5" />,
        activeMatch: '/patterns',
      },
      {
        to: '/docs',
        label: 'Docs',
        icon: <BookOpenIcon className="h-5 w-5" />,
      },
      ...(isDevelopment
        ? [
            {
              to: '/debug/config',
              label: 'Debug Config',
              icon: <WrenchScrewdriverIcon className="h-5 w-5" />,
            },
          ]
        : []),
    ];

    navigation = (
      <nav className="hidden lg:flex lg:items-center lg:gap-2 translate-y-3 rounded-t-lg border border-b-0 border-white/20 px-2 pt-1.5 relative">
        <NavLink to="/docs/getting-started" className={navLinkClassName}>
          <BeakerIcon className="h-5 w-5 mr-1" />
          Beta
        </NavLink>
        <div className="self-center h-6 w-0.5 bg-white/20 mx-1" />
        <NavLink to="/products" className={navLinkClassName}>
          <Boxes className="h-5 w-5 mr-1" />
          Products
        </NavLink>
        <NavLink
          to="/repositories"
          className={navLinkClassName}
          onClick={() => setSelectedRepositoryId(null)}
        >
          <GitBranch className="h-5 w-5 mr-1" />
          Repos
        </NavLink>
        <NavLink to="/projects" className={navLinkClassName}>
          <FolderIcon className="h-5 w-5 mr-1" />
          Projects
        </NavLink>
        <div className="self-center h-6 w-0.5 bg-white/20 mx-1" />
        <NavDropdown
          label="More"
          icon={<EllipsisHorizontalIcon className="h-5 w-5" />}
          items={moreItems}
        />
      </nav>
    );

    if (chrome && chrome.breadcrumbs && chrome.breadcrumbs.length > 0) {
      const breadcrumbs = chrome.breadcrumbs;
      chromeSlot = (
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 truncate text-base text-muted"
        >
          {breadcrumbs.map((crumb, index) => (
            <span key={`${index}-${crumb.label}`} className="flex items-center gap-1.5">
              {index > 0 && <span aria-hidden="true">&rsaquo;</span>}
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-foreground hover:underline">
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current="page" className="font-medium text-foreground">
                  {crumb.label}
                </span>
              )}
            </span>
          ))}
        </nav>
      );
    } else if (chrome) {
      chromeSlot = (
        <h1 className="truncate text-base font-semibold text-foreground">{chrome.title}</h1>
      );
    }

    if (chrome?.subtitle) {
      chromeSubtitle = <p className="truncate text-xs text-muted">{chrome.subtitle}</p>;
    }
  }

  return (
    <header className={clsx({ 'bg-surface': layout === 'default' })}>
      <div className="flex items-center h-16 overflow-hidden bg-surface-elevated shadow-md">
        <div className="flex shrink-0 items-center pl-6 pr-8">
          <Logo />
        </div>
        {!!chromeSlot && (
          <div className="min-w-0 flex-1 truncate">
            {chromeSlot}
            {chromeSubtitle}
          </div>
        )}
        <div className="flex justify-start shrink-0">{navigation}</div>
        {!!content && <div className="flex items-center gap-6 shrink-0 pr-6">{content}</div>}
      </div>
    </header>
  );
}
